import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import { CircularProgress } from '@mui/material';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { Navigate } from 'react-router-dom';
import { loginThunk } from 'features/auth/auth-slice';
import { useTitleHook } from 'hooks/page-title.hook';
import { getDayText } from 'utils/date-format-helpers';
import useConfirmDialog from 'hooks/confirm-dialog.hook';
import { useEffect, useState } from 'react';

const appVersion = process.env.REACT_APP_VERSION || 'development';

function LoginPage() {
  useTitleHook('Войти');
  const dispatch = useDispatch();
  const { pendingRequest, requestFailed, error, user } = useSelector(store => store.auth);
  const confirmDialog = useConfirmDialog();
  const [redirectPath, setRedirectPath] = React.useState(null);
  const passwordCheckedRef = React.useRef(false);

  const [ip, setIp ] = useState(undefined);

  useEffect(() => {
    fetch('/api/settings/my-ip')
      .then(res=>res.json())
      .then(data=>{
        setIp(data.ip);
      });
  }, [setIp]);

  const handleSubmit = (event) => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const loginData = {
      login: formData.get('login'),
      password: formData.get('password')
    };
    dispatch(loginThunk(loginData));
  };

  const checkPasswordExpiration = React.useCallback(async (user) => {
    if (!user || passwordCheckedRef.current) return;

    const passwordDate = user?.createdAtPassword;
    if (passwordDate && !user.isAdmin) {
      const passwordCreatedAt = new Date(passwordDate);
      const passwordExpiresAt = new Date(passwordCreatedAt);
      passwordExpiresAt.setDate(passwordExpiresAt.getDate() + 93);

      const now = new Date();
      const daysLeft = Math.ceil((passwordExpiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

      if (daysLeft <= 14 && daysLeft >= 0) {
        passwordCheckedRef.current = true;

        try {
          await confirmDialog({
            title: `Ваш пароль истекает через ${daysLeft} ${getDayText(daysLeft)}.`,
            content: 'Сменить пароль сейчас?',
            rejectOnCancel: true
          });
          setRedirectPath(`/users/${user.id}`);
        } catch {
          setRedirectPath('/');
        }
        return;
      }
    }
    passwordCheckedRef.current = true;
    setRedirectPath('/');
  }, [confirmDialog]);

  React.useEffect(() => {
    if (user && !passwordCheckedRef.current) {
      checkPasswordExpiration(user);
    }
  }, [user, checkPasswordExpiration]);

  if (redirectPath && user) {
    return <Navigate to={redirectPath} replace />;
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >

        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <LockOutlinedIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Solution 2.0
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Версия: {appVersion}
        </Typography>


        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          {requestFailed && (<Alert severity="error">
            <AlertTitle>Ошибка</AlertTitle>
            {error}
          </Alert>)}
          <TextField
            margin="normal"
            required
            fullWidth
            id="login"
            label="Логин"
            name="login"
            autoComplete="login"
            autoFocus
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Пароль"
            type="password"
            id="password"
            autoComplete="current-password"
          />
          {/* <FormControlLabel
              control={<Checkbox value="remember" color="primary" />}
              label="Remember me"
            /> */}
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
          >
            Вход
          </Button>
          {pendingRequest && (
            <CircularProgress
              size={24}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                marginTop: '-12px',
                marginLeft: '-12px'
              }} />)}
        </Box>
      </Box>
      {!!ip && (
        <Box
          sx={{
            position: 'fixed',
            right: 16,
            bottom: 16,
            p: 2
          }}
        >
          Ваш IP адрес: {String(ip)}
        </Box>
      )}

    </Container>
  );
}

export default LoginPage;
